import { useEffect, useState, useCallback } from "react";
import { Plus, ArrowFatUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, StatusBadge } from "@/components/Shared";
import { ImageCaptionField } from "@/components/ImageCaptionField";
import { CaptionedImage } from "@/components/AuthImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const STATUSES = ["proposed", "in_review", "approved", "implemented", "archived"];
const EMPTY = { title: "", description: "", area: "floor", tags: "", image_id: null, image_caption: "" };

export default function Ideas() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => {
    const { data } = await api.get("/ideas", { params: { status: tab } });
    setRows(data);
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/ideas", {
        title: form.title,
        description: form.description,
        area: form.area,
        image_id: form.image_id,
        image_caption: form.image_caption,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Idea posted to the board");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const vote = async (id) => {
    try {
      await api.post(`/ideas/${id}/vote`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/ideas/${id}/status`, { status });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="ideas-page">
      <PageHeader
        overline="Continuous improvement"
        title="Floor Ideas & Solutions"
        description="Post an idea, upvote the best ones, and watch them move to implemented."
        action={
          <Button data-testid="add-idea-button" onClick={() => setOpen(true)} className="h-11 w-full rounded-none bg-[#002FA7] text-xs font-semibold uppercase tracking-widest hover:bg-[#00227A] sm:h-10 sm:w-auto">
            <Plus size={16} className="mr-2" /> Post idea
          </Button>
        }
      />

      <Tabs value={tab} onValueChange={setTab} className="mb-4 min-w-0">
        <TabsList className="tab-strip no-scrollbar rounded-none bg-slate-100">
          <TabsTrigger data-testid="idea-filter-all" value="all" className="rounded-none text-xs uppercase tracking-wider">All</TabsTrigger>
          {STATUSES.map((s) => (
            <TabsTrigger key={s} data-testid={`idea-filter-${s}`} value={s} className="rounded-none text-xs uppercase tracking-wider">
              {s.replace("_", " ")}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {rows.length === 0 ? (
        <EmptyState testid="ideas-empty" title="No ideas yet" description="Every improvement starts with someone on the floor speaking up." />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {rows.map((idea, idx) => (
            <div
              key={idea.id}
              data-testid={`idea-card-${idea.id}`}
              className={`flex h-full flex-col border border-border bg-white p-5 transition-colors duration-150 hover:border-slate-400 hover:bg-slate-50 ${
                idx === 0 ? "md:col-span-2" : ""
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <StatusBadge testid={`idea-status-${idea.id}`} value={idea.status} />
                  <h3 className="font-display mt-2 text-lg font-semibold tracking-tight text-[#0F172A]">
                    {idea.title}
                  </h3>
                </div>
                <button
                  data-testid={`vote-idea-${idea.id}`}
                  onClick={() => vote(idea.id)}
                  aria-label="Upvote idea"
                  className="flex shrink-0 flex-col items-center border border-border px-3 py-2.5 transition-colors duration-150 hover:border-[#002FA7] hover:bg-[#EEF2FF] active:bg-[#EEF2FF]"
                >
                  <ArrowFatUp
                    size={16}
                    weight={(idea.votes || []).includes(user?.id) ? "fill" : "regular"}
                    className="text-[#002FA7]"
                  />
                  <span data-testid={`idea-votes-${idea.id}`} className="font-mono text-xs font-semibold text-[#0F172A]">
                    {idea.vote_count}
                  </span>
                </button>
              </div>

              <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600">{idea.description}</p>

              <CaptionedImage
                testid={`idea-image-${idea.id}`}
                imageId={idea.image_id}
                caption={idea.image_caption}
                className="max-h-56 w-full border border-border object-cover"
              />

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="overline text-slate-400">{idea.area}</span>
                {(idea.tags || []).map((t) => (
                  <span key={t} className="border border-border px-2 py-0.5 text-[0.7rem] text-slate-500">#{t}</span>
                ))}
              </div>

              <div className="mt-3 flex items-center justify-between gap-2 border-t border-border pt-3">
                <span className="text-xs text-slate-400">{idea.author_name}</span>
                {user?.role === "admin" && (
                  <Select value={idea.status} onValueChange={(v) => setStatus(idea.id, v)}>
                    <SelectTrigger data-testid={`idea-status-select-${idea.id}`} className="h-8 w-32 rounded-none border-slate-300 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Post a floor idea</DialogTitle>
            <DialogDescription>Describe the problem and the solution you would try.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="add-idea-form">
            <div>
              <Label className="overline text-slate-500">Title</Label>
              <Input data-testid="idea-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="mt-1.5 rounded-none border-slate-300" />
            </div>
            <div>
              <Label className="overline text-slate-500">The solution</Label>
              <Textarea data-testid="idea-description-input" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required className="mt-1.5 rounded-none border-slate-300" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="overline text-slate-500">Area</Label>
                <Input data-testid="idea-area-input" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
              </div>
              <div>
                <Label className="overline text-slate-500">Tags (comma separated)</Label>
                <Input data-testid="idea-tags-input" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="mt-1.5 rounded-none border-slate-300" />
              </div>
            </div>
            <ImageCaptionField
              testid="idea-image"
              value={form.image_id}
              caption={form.image_caption}
              onChange={({ image_id, image_caption }) =>
                setForm((f) => ({ ...f, image_id, image_caption }))
              }
            />
            <DialogFooter>
              <Button data-testid="save-idea-button" type="submit" className="rounded-none bg-[#002FA7] text-xs font-semibold uppercase tracking-widest hover:bg-[#00227A]">
                Post idea
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
